import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ResponsiveService, ScreenSize } from '../../services/responsive.service';

@Component({
  selector: 'app-responsive-layout',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './responsive-layout.component.html',
  styleUrl: './responsive-layout.component.css'
})
export class ResponsiveLayoutComponent implements OnInit, OnDestroy, OnChanges {
  @Input() columns: number = 1;
  @Input() gap: string = '1rem';
  @Input() mobileColumns: number = 1;
  @Input() tabletColumns: number = 2;
  @Input() desktopColumns: number = 3;
  @Input() breakpoints: { mobile: number; tablet: number; desktop: number } = {
    mobile: 576,
    tablet: 768,
    desktop: 992
  };
  @Input() alignItems: 'start' | 'center' | 'end' | 'stretch' = 'stretch';
  @Input() justifyContent: 'start' | 'center' | 'end' | 'space-between' | 'space-around' | 'space-evenly' = 'start';

  private destroy$ = new Subject<void>();
  
  currentScreenSize: ScreenSize | null = null;
  layoutClasses: string[] = [];
  gridStyle: { [key: string]: string } = {};

  constructor(private responsiveService: ResponsiveService) {}

  ngOnInit(): void {
    this.responsiveService.screenSize$
      .pipe(takeUntil(this.destroy$))
      .subscribe(screenSize => {
        this.currentScreenSize = screenSize;
        this.updateLayout();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.currentScreenSize) {
      this.updateLayout();
    }
  }

  private updateLayout(): void {
    if (!this.currentScreenSize) return;

    const currentColumns = this.getCurrentColumns();
    
    this.layoutClasses = [
      'responsive-layout',
      `columns-${currentColumns}`,
      `align-${this.alignItems}`,
      `justify-${this.justifyContent}`,
      this.currentScreenSize.isMobile ? 'mobile-layout' : '',
      this.currentScreenSize.isTablet ? 'tablet-layout' : '',
      this.currentScreenSize.isDesktop ? 'desktop-layout' : '',
      this.currentScreenSize.orientation === 'portrait' ? 'portrait' : 'landscape'
    ].filter(Boolean);

    this.gridStyle = {
      display: 'grid',
      'grid-template-columns': `repeat(${currentColumns}, 1fr)`,
      gap: this.gap,
      'align-items': this.alignItems,
      'justify-content': this.justifyContent
    };
  }

  getCurrentColumns(): number {
    if (!this.currentScreenSize) return 1;

    if (this.currentScreenSize.isMobile) {
      return this.mobileColumns;
    } else if (this.currentScreenSize.isTablet) {
      return this.tabletColumns;
    } else {
      return this.desktopColumns;
    }
  }

  // Utility methods for templates
  isMobile(): boolean {
    return this.currentScreenSize?.isMobile || false;
  }

  isTablet(): boolean {
    return this.currentScreenSize?.isTablet || false;
  }

  isDesktop(): boolean {
    return this.currentScreenSize?.isDesktop || false;
  }

  isPortrait(): boolean {
    return this.currentScreenSize?.orientation === 'portrait' || false;
  }

  isLandscape(): boolean {
    return this.currentScreenSize?.orientation === 'landscape' || false;
  }

  getScreenInfo(): ScreenSize | null {
    return this.currentScreenSize;
  }

  // Helper method for child components to get current breakpoint
  getCurrentBreakpoint(): 'mobile' | 'tablet' | 'desktop' {
    if (!this.currentScreenSize) return 'mobile';
    
    if (this.currentScreenSize.isMobile) return 'mobile';
    if (this.currentScreenSize.isTablet) return 'tablet';
    return 'desktop';
  }
}